import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { isMobile } from 'react-device-detect';
import { Popover } from '@barges/react';
import cn from 'classnames';
import {
  convertErrorArrayToString, passwordIsValid
} from '../../config/util';
import PromoCodeSignUp from '../promo_code_sign_up';
import Social from './inner_components/social';
import PhoneNumberCta from './inner_components/phone_number_cta';
import classes from './classes.module.scss';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import Const from '../../config/const';
import ForgotPassword from '../forgot_password';
import LogIn from './inner_components/log_in';
import SignUp from './inner_components/sign_up';
import PhoneEntry from './inner_components/phone_entry';
import PhoneVerification from './inner_components/phone_verification';
import BackupEmailScreen from './inner_components/backup_email_screen';
import { appRoutes, applicationRoutes } from '../../config/app_routes';
import {
  emailIsValid, withCaptcha, hideBodyScroll, unlockBodyScroll
} from '../../../common/config/utils';
import { dismissPopup } from '../../hooks/useGeneralPopup';
import Spiner from '../../../common/helpers/spiner';
import Alert from '../../../common/helpers/alert';
import { appLogo } from '../../logo/logo';
import { getImageByEnvironment } from '../../project_items/image_renderer/image_renderer';
import { SvgIcon } from '../../../common/sprites';

const { shared } = window;

const isAffiliatePromo = ({ userShowMatchUpBonusInfo, matchUpBonusInfo }) => !!(matchUpBonusInfo && Object.keys(matchUpBonusInfo).length !== 0 && (userShowMatchUpBonusInfo === null || userShowMatchUpBonusInfo));

class Registration extends PureComponent {
  static propTypes = {
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    logIn: PropTypes.func.isRequired,
    logInByPhone: PropTypes.func.isRequired,
    register: PropTypes.func.isRequired,
    registerByPhone: PropTypes.func.isRequired,
    clearError: PropTypes.func.isRequired,
    authSocial: PropTypes.func.isRequired,
    authPendingAction: PropTypes.func,
    authPendingCloseAction: PropTypes.func,
    t: PropTypes.func.isRequired,
    visible: PropTypes.bool,
    displayRegister: PropTypes.func.isRequired,
    signUpFlow: PropTypes.string,
    currentScreen: PropTypes.string.isRequired,
    screenStack: PropTypes.array.isRequired,
    changeScreen: PropTypes.func.isRequired,
    navigationPop: PropTypes.func.isRequired,
    authComplete: PropTypes.bool,
    loadHomePage: PropTypes.func.isRequired,
    clearAffiliatePromoPopup: PropTypes.func.isRequired,
    authClearPendingAction: PropTypes.func.isRequired,
    authErrorActionCreator: PropTypes.func.isRequired,
    gdprSwitcherValueChanged: PropTypes.func.isRequired,
    consentedToGDPR: PropTypes.bool,
    shouldShowGDPRConsent: PropTypes.func.isRequired,
    userShowMatchUpBonusInfo: PropTypes.bool,
    updateCurrentScreen: PropTypes.func.isRequired,
    router: PropTypes.object.isRequired,
    currentPath: PropTypes.object.isRequired,
    inEu: PropTypes.bool,
    matchUpBonusInfo: PropTypes.object,
    googleConfig: PropTypes.object,
    smsSettings: PropTypes.object.isRequired
  };

  static defaultProps = {
    error: null,
    authPendingAction: null,
    authPendingCloseAction: null,
    visible: false,
    signUpFlow: 'menu',
    authComplete: false,
    consentedToGDPR: false,
    userShowMatchUpBonusInfo: null,
    inEu: true,
    matchUpBonusInfo: null,
    googleConfig: null
  };

  state = {
    email: '',
    password: '',
    confirmEmail: '',
    rememberMe: false,
    phoneNumber: null,
    timerEndsAt: null,
    agreedToTerms: false,
    consentToMarketingSms: false,
    smsAttempts: 0
  };

  componentDidMount() {
    const { visible } = this.props;
    if (visible) {
      hideBodyScroll();
    }
    // Add event listeners for input focus/blur to prevent scrolling when keyboard opens
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    document.addEventListener('focusin', this.handleInputFocus);
    document.addEventListener('focusout', this.handleInputBlur);
  }

  componentDidUpdate(prevProps) {
    const {
      authComplete,
      authPendingAction,
      currentScreen,
      userShowMatchUpBonusInfo,
      updateCurrentScreen,
      matchUpBonusInfo,
      visible
    } = this.props;

    // Handle body scroll lock
    if (visible && !prevProps.visible) {
      hideBodyScroll();
    } else if (!visible && prevProps.visible) {
      unlockBodyScroll();
    }

    if (currentScreen === Const.authScreens.signUp && isAffiliatePromo({ userShowMatchUpBonusInfo, matchUpBonusInfo })) {
      updateCurrentScreen(Const.authScreens.signUpAffiliatePromo);
    }

    if (authComplete && !prevProps.authComplete) {
      if (authPendingAction) {
        authPendingAction();
        return;
      }
      this.redirectToPsychics(true);
    }
  }

  componentWillUnmount() {
    unlockBodyScroll();
    // Remove event listeners
    document.removeEventListener('focusin', this.handleInputFocus);
    document.removeEventListener('focusout', this.handleInputBlur);

    // Clean up keyboard marker and viewport height
    if (isMobile) {
      if (this.keyboardViewportResizeCleanup) {
        this.keyboardViewportResizeCleanup();
        this.keyboardViewportResizeCleanup = null;
      }
      if (this.keyboardViewportHeightTimeout) {
        clearTimeout(this.keyboardViewportHeightTimeout);
        this.keyboardViewportHeightTimeout = null;
      }
      document.body.removeAttribute('data-keyboard-open');
      document.documentElement.style.removeProperty('--keyboard-viewport-height');
    }
  }

  handleInputFocus = (e) => {
    // Only handle if focus is on an input/textarea inside the modal
    const { visible } = this.props;
    if (!visible) return;

    const { target } = e;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      // Check if the input is inside the registration modal
      const modal = target.closest('.registrationPopover, dialog.registrationPopover');
      if (modal && isMobile) {
        document.body.setAttribute('data-keyboard-open', 'true');

        const setKeyboardViewportHeight = () => {
          if (typeof window.visualViewport !== 'undefined') {
            document.documentElement.style.setProperty(
              '--keyboard-viewport-height',
              `${ window.visualViewport.height }px`
            );
          }
        };

        const stopListening = () => {
          if (this.keyboardViewportResizeCleanup) {
            this.keyboardViewportResizeCleanup();
            this.keyboardViewportResizeCleanup = null;
          }
          if (this.keyboardViewportHeightTimeout) {
            clearTimeout(this.keyboardViewportHeightTimeout);
            this.keyboardViewportHeightTimeout = null;
          }
        };

        // Set height once on first visualViewport resize (keyboard opened).
        // Do not update again — later layout changes can make the toolbar disappear on iOS.
        const onResize = () => {
          requestAnimationFrame(() => {
            setKeyboardViewportHeight();
            stopListening();
          });
        };

        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', onResize);
          this.keyboardViewportResizeCleanup = () => {
            window.visualViewport.removeEventListener('resize', onResize);
          };
        }

        // Fallback: set after short delay if no resize fires (e.g. keyboard already open)
        this.keyboardViewportHeightTimeout = setTimeout(() => {
          this.keyboardViewportHeightTimeout = null;
          if (this.keyboardViewportResizeCleanup) {
            setKeyboardViewportHeight();
            stopListening();
          }
        }, 250);
      }
    }
  };

  handleInputBlur = (e) => {
    const { visible } = this.props;
    if (!visible) return;

    const { target } = e;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      const modal = target.closest('.registrationPopover, dialog.registrationPopover');
      if (modal && isMobile) {
        if (this.keyboardViewportResizeCleanup) {
          this.keyboardViewportResizeCleanup();
          this.keyboardViewportResizeCleanup = null;
        }
        if (this.keyboardViewportHeightTimeout) {
          clearTimeout(this.keyboardViewportHeightTimeout);
          this.keyboardViewportHeightTimeout = null;
        }
        setTimeout(() => {
          const { activeElement } = document;
          if (!activeElement
              || (activeElement.tagName !== 'INPUT'
              && activeElement.tagName !== 'TEXTAREA')) {
            document.body.removeAttribute('data-keyboard-open');
            document.documentElement.style.removeProperty('--keyboard-viewport-height');
          }
        }, 150);
      }
    }
  };

  checkInputErrors = () => {
    const { email, confirmEmail, password } = this.state;
    const { authErrorActionCreator, t, currentScreen } = this.props;
    if (!passwordIsValid(password, true)) {
      authErrorActionCreator({ password: [t('error.registration_please_enter_your_password')] });
    }
    if (
      (currentScreen === Const.authScreens.signUp
        || currentScreen === Const.authScreens.signUpAffiliatePromo)
      && email !== confirmEmail) {
      authErrorActionCreator({ email: [t('error.registration_email_not_much')] });
    }
    if (!emailIsValid(email)) {
      authErrorActionCreator({ email: [t('error.registration_please_enter_a_valid_email_address')] });
    }
  };

  onFieldChanged = e => {
    const { error, clearError } = this.props;
    if (error) clearError();
    this.setState({ [e.target.id]: e.target.value });
  };

  onCancelClicked = () => {
    const {
      currentScreen, screenStack, navigationPop, authClearPendingAction,
      authPendingCloseAction, clearError
    } = this.props;

    if (currentScreen === Const.authScreens.signUpAffiliatePromo) {
      authClearPendingAction();
    }

    if (screenStack.length <= 0) {
      dismissPopup();
      authClearPendingAction();
      if (authPendingCloseAction) {
        authPendingCloseAction();
      }
    }

    if (screenStack.length >= 1) {
      navigationPop();
      clearError();
      return;
    }

    this.redirectToPsychics();
    clearError();
  };

  redirectToPsychics = (authCompleteSusses) => {
    const {
      loadHomePage,
      displayRegister,
      router: { navigate },
      currentPath: { routeKey }
    } = this.props;

    displayRegister({ shouldDisplay: false });

    switch (true) {
      case routeKey === appRoutes.home:
        loadHomePage(true);
        break;
      case [appRoutes.signin, appRoutes.register, appRoutes.signinPhone].includes(routeKey):
        navigate(appRoutes.home, { options: { replace: true } });
        break;
      case applicationRoutes[routeKey]?.isProtected && !authCompleteSusses:
        navigate(appRoutes.psychics, { options: { replace: true } });
        break;
      default: break;
    }
  };

  onLoginClicked = () => {
    log(LOG_LEVELS.INFO, 'Register', 'on login');
    const { logIn, signUpFlow } = this.props;
    const { email, password, rememberMe } = this.state;
    withCaptcha('login', (token) => logIn(email, password, rememberMe, signUpFlow, token));
  };

  onRegisterClicked = () => {
    log(LOG_LEVELS.INFO, 'Register', 'on register');
    const { register, signUpFlow } = this.props;
    const { email, password, rememberMe } = this.state;
    withCaptcha('login', (token) => register(email, password, rememberMe, signUpFlow, token));
  };

  onOkClickSignUpPromoRead = () => {
    const { displayRegister } = this.props;
    displayRegister({ shouldDisplay: false });
  };

  renderSignUpPromoRead = () => (
    <PromoCodeSignUp
      onOkClickSignUpPromoRead={ this.onOkClickSignUpPromoRead }
    />
  );

  renderGeneralError = () => {
    const { error, clearError, currentScreen } = this.props;

    if (!error) return null;

    // Don't show general error modal for field-specific errors
    if (error && (error.hasOwnProperty('email') || error.hasOwnProperty('password'))) return null;

    // Don't show general error modal for phone verification errors (confirmationCode, fullMessages)
    // These should be displayed inline under the code inputs
    if (currentScreen === Const.authScreens.phoneVerification) {
      if (error && (error.hasOwnProperty('confirmationCode') || error.hasOwnProperty('fullMessages') || error.hasOwnProperty('code'))) {
        return null;
      }
    }

    // Don't show general error modal for backup email screen errors (email already exists - 409)
    // These should be displayed inline under the email input
    if (currentScreen === Const.authScreens.backupEmailRequest) {
      if (error && (error.hasOwnProperty('email') || error.status === 409)) {
        return null;
      }
    }

    const errorString = error && convertErrorArrayToString(
      Object.keys(error).map((key) => error[key])
    );
    log(LOG_LEVELS.ERROR, 'Register', `on error: ${ errorString }`);
    return (
      <Alert
        errorString={ errorString }
        onClose={ clearError }
      />
    );
  };

  navigateToSignIn = () => {
    const { changeScreen } = this.props;
    changeScreen(Const.authScreens.signIn);
    this.screenChanged();
  };

  navigateToSignUp = () => {
    const { changeScreen } = this.props;
    changeScreen(Const.authScreens.signUp);
    this.screenChanged();
  };

  navigateToForgotPassword = () => {
    const { changeScreen } = this.props;
    changeScreen(Const.authScreens.forgotPassword);
  };

  handlePhoneSubmit = (phoneNumber, agreedToTerms, savedConsentToMarketingSms) => {
    const { changeScreen } = this.props;
    // Save phone number and agreedToTerms to state
    // Initialize SMS attempts counter if it's 0 (first time entering SMS flow)
    this.setState((prevState) => ({
      phoneNumber,
      agreedToTerms,
      consentToMarketingSms: savedConsentToMarketingSms,
      smsAttempts: prevState.smsAttempts === 0 ? 0 : prevState.smsAttempts
    }));
    // Navigate to verification screen
    changeScreen(Const.authScreens.phoneVerification);
    this.screenChanged();
  };

  incrementSmsAttempts = () => {
    // Get current value and calculate new value synchronously
    const { smsAttempts: prevAttempts = 0 } = this.state;
    const newAttempts = prevAttempts + 1;

    // Update state asynchronously
    this.setState({ smsAttempts: newAttempts });

    // Return the new value immediately (we calculated it synchronously)
    return newAttempts;
  };

  handleTimerEndsAtChange = (timerEndsAt) => {
    // Save timerEndsAt to state so it persists between components
    this.setState({ timerEndsAt });
  };

  handlePhoneEntryBack = () => {
    const { navigationPop, screenStack } = this.props;
    // If there's a previous screen in stack, go back
    if (screenStack.length > 1) {
      navigationPop();
    } else {
      // Otherwise go to sign in
      this.navigateToSignIn();
    }
    this.screenChanged();
  };

  handlePhoneVerificationBack = () => {
    const { changeScreen } = this.props;
    // Go back to phone entry screen
    changeScreen(Const.authScreens.phoneEntry);
    this.screenChanged();
  };

  handlePhoneVerificationSuccess = (confirmationCode) => {
    // Phone verification successful - navigate to backup email screen
    const { changeScreen } = this.props;
    this.setState({ confirmationCode });
    changeScreen(Const.authScreens.backupEmailRequest);
    this.screenChanged();
  };

  handleBackupEmailCodeSent = () => {
    // Reset SMS attempts counter when backup email code is sent (SMS flow completed)
    this.setState({
      phoneNumber: null,
      confirmationCode: null,
      timerEndsAt: null,
      agreedToTerms: false,
      smsAttempts: 0
    });
  };

  handleBackupEmailSkip = () => {
    // User skipped backup email - complete registration
    // Reset SMS attempts counter when SMS flow is closed
    const { loadHomePage } = this.props;
    // Clear phone number, timer, agreedToTerms, and reset SMS attempts from state
    this.setState({
      phoneNumber: null,
      timerEndsAt: null,
      agreedToTerms: false,
      consentToMarketingSms: false
    });
    // Load home page
    loadHomePage();
  };

  handlePhoneResend = () => {
    const { phoneNumber } = this.state;
    if (!phoneNumber) {
      // If no phone number, go back to entry screen
      this.handlePhoneVerificationBack();
    }
    // Resend SMS code - this will be handled by PhoneVerification component
    // The component uses usePhoneVerification hook which has sendSmsCode method
  };

  screenChanged = () => {
    const { clearError } = this.props;
    clearError();
  };

  facebookResponse = facebookToken => {
    log(LOG_LEVELS.INFO, 'FB Auth', 'We get response from FB and start sign In');
    const { rememberMe } = this.state;
    const { authSocial, signUpFlow } = this.props;
    authSocial({ facebookToken, rememberMe }, signUpFlow);
  };

  googleResponse = googleToken => {
    log(LOG_LEVELS.INFO, 'Google Auth', 'We get response from Google and start sign In');
    const { rememberMe } = this.state;
    const { authSocial, signUpFlow } = this.props;
    authSocial({ googleToken, rememberMe }, signUpFlow);
  };

  renderSocial = () => {
    const {
      clearError, error, consentedToGDPR, shouldShowGDPRConsent,
      gdprSwitcherValueChanged, googleConfig, changeScreen, t, loading, currentScreen, smsSettings
    } = this.props;
    return (
      <div>
        { currentScreen === Const.authScreens.signIn
         && smsSettings?.allowedSmsCountries?.length > 0 && (
         <PhoneNumberCta
           changeScreen={ changeScreen }
           t={ t }
           loading={ loading }
         />
        )}
        <Social
          facebookCallback={ this.facebookResponse }
          googleCallback={ this.googleResponse }
          inEu
          signUpSwitcherChecked={ consentedToGDPR }
          clearError={ clearError }
          error={ error }
          gdprSwitcherValueChanged={ gdprSwitcherValueChanged }
          shouldShowGDPRConsent={ shouldShowGDPRConsent }
          googleConfig={ googleConfig }
        />
      </div>
    );
  };

  onRemeberMeCheckedChanged = (e) => {
    this.setState({ rememberMe: e.target.checked });
  };

  renderRememberMe = () => {
    const { rememberMe } = this.state;
    const { t, currentScreen } = this.props;
    return (
      <div className={ classes.rememberMeContainer }>
        <input type="checkbox" checked={ rememberMe } onChange={ this.onRemeberMeCheckedChanged } className={ classes.rememberMeCheckboxInput } />
        <span className={ classes[`remeberMeMargin${ currentScreen }`] }>{t('remember_me')}</span>
      </div>
    );
  };

  renderContent = () => {
    const {
      currentScreen, loading, error, t, changeScreen, clearError,
      consentedToGDPR, shouldShowGDPRConsent, matchUpBonusInfo, registerByPhone, logInByPhone, signUpFlow, smsSettings
    } = this.props;
    const {
      email, password, confirmEmail, phoneNumber, confirmationCode, timerEndsAt, agreedToTerms, consentToMarketingSms
    } = this.state;
    switch (currentScreen) {
      case Const.authScreens.signIn:
        return (
          <LogIn
            email={ email }
            password={ password }
            loading={ loading }
            error={ error }
            t={ t }
            onFieldChanged={ this.onFieldChanged }
            rememberMe={ this.renderRememberMe() }
            social={ this.renderSocial() }
            changeScreen={ changeScreen }
            onLogin={ this.onLoginClicked }
            checkInputErrors={ this.checkInputErrors }
            clearError={ clearError }
          />
        );
      case Const.authScreens.signUp:
      case Const.authScreens.signUpAffiliatePromo:
        return (
          <SignUp
            email={ email }
            password={ password }
            confirmEmail={ confirmEmail }
            switcherChecked={ consentedToGDPR }
            shouldShowGDPRConsent={ shouldShowGDPRConsent }
            loading={ loading }
            error={ error }
            t={ t }
            onRegister={ this.onRegisterClicked }
            onFieldChanged={ this.onFieldChanged }
            rememberMe={ this.renderRememberMe() }
            social={ this.renderSocial() }
            changeScreen={ changeScreen }
            checkInputErrors={ this.checkInputErrors }
            clearError={ clearError }
            theme={ currentScreen }
            userMatchUpBonusInfo={ matchUpBonusInfo }
            showLoginByPhone={ smsSettings?.allowedSmsCountries?.length > 0 }
          />
        );
      case Const.authScreens.promoRead:
        return this.renderSignUpPromoRead();
      case Const.authScreens.forgotPassword:
        return <ForgotPassword />;
      case Const.authScreens.phoneEntry: {
        const { smsAttempts: entrySmsAttempts } = this.state;
        return (
          <PhoneEntry
            onPhoneSubmit={ this.handlePhoneSubmit }
            onBack={ this.handlePhoneEntryBack }
            t={ t }
            clearError={ clearError }
            loading={ loading }
            error={ error }
            smsSettings={ smsSettings }
            savedPhoneNumber={ phoneNumber }
            savedAgreedToTerms={ agreedToTerms }
            savedTimerEndsAt={ timerEndsAt }
            onTimerEndsAtChange={ this.handleTimerEndsAtChange }
            smsAttempts={ entrySmsAttempts }
            incrementSmsAttempts={ this.incrementSmsAttempts }
          />
        );
      }
      case Const.authScreens.phoneVerification: {
        const { smsAttempts: verificationSmsAttempts } = this.state;
        return (
          <PhoneVerification
            phoneNumber={ phoneNumber }
            consentToMarketingSms={ consentToMarketingSms }
            onCodeSubmit={ this.handlePhoneVerificationSuccess }
            onResendClick={ this.handlePhoneResend }
            onBack={ this.handlePhoneVerificationBack }
            t={ t }
            clearError={ clearError }
            loading={ loading }
            logInByPhone={ logInByPhone }
            smsAttempts={ verificationSmsAttempts }
            incrementSmsAttempts={ this.incrementSmsAttempts }
            signUpFlow={ signUpFlow }
            savedTimerEndsAt={ timerEndsAt }
            onTimerEndsAtChange={ this.handleTimerEndsAtChange }
            { ...this.props }
            error={ error?.fullMessages?.[0] }

          />
        );
      }
      case Const.authScreens.backupEmailRequest:
        return (
          <BackupEmailScreen
            phoneNumber={ phoneNumber }
            confirmationCode={ confirmationCode }
            consentedToGDPR={ agreedToTerms }
            consentToMarketingSms={ consentToMarketingSms }
            onEmailCodeSent={ this.handleBackupEmailCodeSent }
            onSkip={ this.handleBackupEmailSkip }
            t={ t }
            clearError={ clearError }
            loading={ loading }
            registerByPhone={ registerByPhone }
            shouldShowGDPRConsent={ shouldShowGDPRConsent }
          />
        );
      default:
        return null;
    }
  };

  renderBackButton = () => {
    const { currentScreen } = this.props;
    if (currentScreen !== Const.authScreens.promoRead) {
      return (
        <div className={ classes.cancelContainer }>
          <button
            type="button"
            onClick={ this.onCancelClicked }
            className={ classes.cancelButton }
          >
            <SvgIcon className={ classes.cancelButtonImg } id="back_arrow" size={ 25 } />
          </button>
        </div>
      );
    }
    return null;
  };

  renderTopLogo = () => (
    <div
      className={ isMobile ? classes.topLogoContainerMobile : classes.topLogoContainerBrowser }
    >
      <img src={ appLogo() } alt="" className={ classes.topLogoImg } />
    </div>
  );

  renderLogo = () => {
    const { currentScreen } = this.props;
    if (
      currentScreen === Const.authScreens.phoneEntry
      || currentScreen === Const.authScreens.phoneVerification
      || currentScreen === Const.authScreens.backupEmailRequest
    ) return null;

    if (
      currentScreen === Const.authScreens.signUpAffiliatePromo
    ) return isMobile ? this.renderTopLogo() : null;

    return (
      <div className={ classes.logoContainer }>
        <img src={ this.getLogo() } alt="" className={ classes.logoImg } />
      </div>
    );
  };

  renderLoading = () => {
    const { loading, currentScreen } = this.props;
    if (currentScreen === Const.authScreens.phoneVerification) return null;
    if (loading) return <Spiner loaderColor={ shared.appColor } />;
    return null;
  };

  getLogo = () => {
    const { currentScreen } = this.props;
    return currentScreen === Const.authScreens.promoRead ? getImageByEnvironment('signup_complete_checkmark') : appLogo();
  };

  getHeight = () => {
    const { currentScreen } = this.props;
    if (currentScreen === Const.authScreens.signUpAffiliatePromo) return '100%';
    return '';
  };

  getBackgroundColor = () => {
    const { currentScreen } = this.props;
    if (currentScreen !== Const.authScreens.signUpAffiliatePromo) return '#FFFFFF';
    return '#1b1945';
  };

  render() {
    const { visible, currentScreen } = this.props;

    return (
      <Popover
        isOpen={ visible }
        renderTopOverlay={ currentScreen === Const.authScreens.signUpAffiliatePromo && !isMobile && this.renderTopLogo }
        renderBackButton={ this.renderBackButton }
        className={ classes.registrationPopover }
        overlayClassName={ classes.overlayContainer }
        contentClassName={ classes.registrationPopoverContent }
      >
        <div className={ classes.popover }>
          <div
            className={ isMobile ? classes.mobileContainer : classes.browserContainer }
            style={ { '--backgroundColor': this.getBackgroundColor() } }
          >
            <div className={ cn(classes.contentHolder, classes[`contentHolder${ currentScreen }`]) }>
              <div style={ { zIndex: 9 } }>
                {this.renderLogo()}
                {this.renderContent()}
              </div>
              {this.renderGeneralError()}
            </div>
            {this.renderLoading()}
          </div>
        </div>
      </Popover>
    );
  }
}

export default Registration;
